// src/pages/Landing.jsx
import React, { useEffect, useRef, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from './authContext';
import { doSignInWithEmailAndPassword, doSignInWithGoogle } from './auth';
import newCover from './coverNewTransparent.png';
import featuresVideo from './features.mov'; // ✅ if keeping inside src/pages
import rentreveal from './rentreveal.mov'; // ✅ if keeping inside src/pages
import aiassistant from './aiassistant.mov'; // ✅ if keeping inside src/pages
import dealexplorerai from './dealexplorerai.mov'; // ✅ if keeping inside src/pages
// ADD with your other imports
import { doCreateUserWithEmailAndPassword } from './auth';
// at the top
import {
  MagnifyingGlassIcon,
  HomeIcon,
  ChartBarIcon,
  MicrophoneIcon
} from '@heroicons/react/24/outline';

/* ===========================================
   Brand
=========================================== */
const BRAND_GREEN = '#06b22b';

/* ===========================================
   Inline SVG Icons (no dependencies)
=========================================== */
// 8 feature tile icons
const FeatureIcons = [
  // Magnifying glass
  (props) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...props}>
      <path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
        d="M21 21l-4.35-4.35m0 0A7.5 7.5 0 1010.5 3a7.5 7.5 0 006.15 13.65z" />
    </svg>
  ),
  // Home
  (props) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...props}>
      <path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
        d="M3 12l9-9 9 9v7a3 3 0 01-3 3h-2a2 2 0 01-2-2v-3H10v3a2 2 0 01-2 2H6a3 3 0 01-3-3v-7z" />
    </svg>
  ),
  // Chart bar
  (props) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...props}>
      <path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
        d="M4 19h16M6 17V9m6 8V5m6 12v-6" />
    </svg>
  ),
  // Dollar / currency
  (props) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...props}>
      <path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
        d="M12 8c-2.5 0-3.5 1.2-3.5 2.5 0 3 7 1.5 7 5 0 1.4-1.2 2.5-3.5 2.5s-3.5-1-3.5-2.5M12 5v2m0 10v2" />
    </svg>
  ),
  // Calculator
  (props) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...props}>
      <rect x="5" y="3" width="14" height="18" rx="2" />
      <path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
        d="M8 7h8M8 11h2m4 0h2M8 15h2m4 0h2" />
    </svg>
  ),
  // Chat bubbles
  (props) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...props}>
      <path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
        d="M8 10h8M8 14h5M5 20l3-3h9a3 3 0 003-3V7a3 3 0 00-3-3H7a3 3 0 00-3 3v13z" />
    </svg>
  ),
  // Users / skip tracing
  (props) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...props}>
      <path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
        d="M17 21v-2a4 4 0 00-4-4H7a4 4 0 00-4 4v2M7 7a4 4 0 118 0 4 4 0 01-8 0zM20 8v6m3-3h-6" />
    </svg>
  ),
  // Banknotes
  (props) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...props}>
      <rect x="3" y="7" width="18" height="10" rx="2" />
      <circle cx="12" cy="12" r="2.5" />
      <path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
        d="M7 9.5v0M17 14.5v0" />
    </svg>
  ),
];

// 3 stage card icons (AI Feature 1–3)
const StageIcons = [
  // Bulb (insight)
  (props) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...props}>
      <path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
        d="M9 18h6m-5 3h4M12 2a7 7 0 00-4 12c.6.6 1 1.6 1 2.5V17h6v-.5c0-.9.4-1.9 1-2.5A7 7 0 0012 2z" />
    </svg>
  ),
  // Rocket (speed)
  (props) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...props}>
      <path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
        d="M5 15l4 4m0 0l2-6 6-6a4 4 0 00-6-6l-6 6-6 2 4 4" />
    </svg>
  ),
  // Mic (voice)
  (props) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...props}>
      <rect x="9" y="2" width="6" height="12" rx="3" />
      <path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
        d="M5 11a7 7 0 0014 0M12 19v3" />
    </svg>
  ),
];

/* ===========================================
   Static data (no hooks)
=========================================== */

const STAGE_ITEMS = [
  {
    title: 'Rent Reveal AI',
    copy: 'Instantly uncover rental value and market insights...',
    icon: MagnifyingGlassIcon,
    media: 'https://images.unsplash.com/photo-1517976487492-576ea6b2936a?q=80&w=1600&auto=format&fit=crop',
  },
  {
    title: 'Deal Explorer AI',
    copy: 'Analyze deals in seconds with AI-powered comps, cash flow and ROI...',
    icon: HomeIcon, // 👈 little house icon
    media: 'https://images.unsplash.com/photo-1582407947304-fd86f028f716?q=80&w=1600&auto=format&fit=crop',
  },
  {
    title: 'Elevate AI with Voice',
    copy: 'Ask questions and get instant property insights hands-free...',
    icon: MicrophoneIcon,
    media: 'https://images.unsplash.com/photo-1527443154391-507e9dc6c5cc?q=80&w=1600&auto=format&fit=crop',
  },
];

/* ===========================================
   Utilities (no external libs)
=========================================== */
const useTopProgress = () => {
  const [p, setP] = useState(0);
  useEffect(() => {
    const onScroll = () => {
      const h = document.documentElement;
      const max = h.scrollHeight - h.clientHeight;
      setP(max > 0 ? h.scrollTop / max : 0);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  return p;
};
// Shield with check (for Insurance)
const ShieldCheckIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...props}>
    <path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      d="M12 22c6-2 8-5.5 8-10V7l-8-4-8 4v5c0 4.5 2 8 8 10" />
    <path strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      d="M9 12l2 2 4-4" />
  </svg>
);

const useInView = (opts = { threshold: 0.15 }) => {
  const ref = useRef(null);
  const [v, setV] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const io = new IntersectionObserver(([e]) => e.isIntersecting && setV(true), opts);
    io.observe(ref.current);
    return () => io.disconnect();
  }, [opts]);
  return { ref, v };
};

const Fade = ({ delay = 0, className = '', children }) => {
  const { ref, v } = useInView();
  return (
    <div
      ref={ref}
      style={{ transitionDelay: `${delay}ms` }}
      className={`opacity-0 translate-y-6 duration-700 ease-out will-change-transform ${
        v ? 'opacity-100 translate-y-0' : ''
      } ${className}`}
    >
      {children}
    </div>
  );
};

/* ===========================================
   Sticky AI Stage (pinned phone + scroll cards)
=========================================== */
const Stage = ({ items }) => {
  const wrapRef = useRef(null);
  const [idx, setIdx] = useState(0);
  const phoneRef = useRef(null);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const cards = Array.from(el.querySelectorAll('[data-stage-card]'));
    const obs = new IntersectionObserver(
      (entries) => {
        const top = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (!top) return;
        const i = Number(top.target.getAttribute('data-stage-card'));
        setIdx(i);
      },
      { root: null, threshold: [0.25, 0.6, 0.9], rootMargin: '-25% 0px -35% 0px' }
    );
    cards.forEach((c) => obs.observe(c));
    return () => obs.disconnect();
  }, []);

  // Subtle tilt
  useEffect(() => {
    const el = phoneRef.current;
    if (!el) return;
    const onMove = (e) => {
      const r = el.getBoundingClientRect();
      const x = (e.clientX - (r.left + r.width / 2)) / r.width;
      const y = (e.clientY - (r.top + r.height / 2)) / r.height;
      el.style.transform = `perspective(1000px) rotateX(${y * -6}deg) rotateY(${x * 8}deg)`;
    };
    const reset = () => (el.style.transform = 'perspective(1000px) rotateX(0) rotateY(0)');
    el.addEventListener('mousemove', onMove);
    el.addEventListener('mouseleave', reset);
    return () => {
      el.removeEventListener('mousemove', onMove);
      el.removeEventListener('mouseleave', reset);
    };
  }, []);

  const isVideo = (src) => /\.mp4($|\?)/i.test(src);

  return (
    <div ref={wrapRef} className="mx-auto max-w-7xl px-4 grid md:grid-cols-2 gap-10">
      {/* Sticky phone with sliding screens */}
      <div className="relative">
        <div className="md:sticky md:top-24">
          <div
            ref={phoneRef}
            className="relative mx-auto w-[320px] h-[650px] rounded-[2rem] border border-black/10 shadow-2xl overflow-hidden bg-white transition-transform duration-300"
            style={{ transformStyle: 'preserve-3d' }}
          >
            <div className="absolute top-0 left-0 right-0 h-7 bg-black/5 z-20" />
            <div
              className="absolute inset-0 flex"
              style={{
                width: `${items.length * 100}%`,
                transform: `translateX(-${idx * (100 / items.length)}%)`,
                transition: 'transform 700ms cubic-bezier(.2,.8,.2,1)',
              }}
            >
              {items.map((it, i) => (
                <div key={i} className="relative w-full h-full flex-[0_0_100%]">
                  {isVideo(it.media) ? (
                    <video
                      className="absolute inset-0 w-full h-full object-cover"
                      src={it.media}
                      autoPlay
                      muted
                      loop
                      playsInline
                    />
                  ) : (
                    <img
                      src={it.media}
                      alt={it.title}
                      className="absolute inset-0 w-full h-full object-cover"
                      draggable={false}
                    />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-b from-black/5 via-transparent to-black/10 pointer-events-none" />
                </div>
              ))}
            </div>
            <div
              aria-hidden
              className="absolute -z-10 blur-3xl w-[520px] h-[520px] rounded-full"
              style={{ backgroundColor: `${BRAND_GREEN}33`, right: -140, top: -60 }}
            />
          </div>

          {/* progress dots */}
          <div className="mt-4 flex items-center justify-center gap-2">
            {items.map((_, i) => (
              <button
                key={i}
                aria-label={`Go to slide ${i + 1}`}
                onClick={() => setIdx(i)}
                className={`h-2.5 rounded-full transition-all ${
                  i === idx ? 'w-8' : 'w-2.5'
                }`}
                style={{ backgroundColor: i === idx ? BRAND_GREEN : '#d1d5db' }}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Scroll cards */}
      <div className="space-y-8">
        {items.map((it, i) => {
          const Icon = StageIcons[i] || StageIcons[0];
          return (
            <div
              key={i}
              data-stage-card={i}
              className={`p-6 rounded-2xl border shadow-xl bg-white transition-colors ${
                i === idx ? 'border-black/10' : 'border-black/10'
              }`}
              style={{ borderColor: i === idx ? `${BRAND_GREEN}99` : 'rgba(0,0,0,0.1)' }}
            >
<div className="flex items-center gap-3">
  {it.icon && <it.icon className="w-6 h-6 text-[#06b22b]" />}
  <div className="font-semibold text-lg text-gray-900">{it.title}</div>
</div>
              <p className="mt-2 text-gray-600">{it.copy}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const CountUp = ({ to, prefix = '', suffix = '', duration = 1200, className = '' }) => {
  const [n, setN] = useState(0);
  const started = useRef(false);
  const { ref, v } = useInView({ threshold: 0.3 });
  useEffect(() => {
    if (!v || started.current) return;
    started.current = true;
    const t0 = performance.now();
    const animate = (t) => {
      const p = Math.min(1, (t - t0) / duration);
      const eased = 0.5 - Math.cos(p * Math.PI) / 2;
      setN(Math.round(to * eased));
      if (p < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [v, to, duration]);
  return (
    <div ref={ref} className={className}>
      {prefix}
      {n.toLocaleString()}
      {suffix}
    </div>
  );
};

/* ===========================================
   Main Landing
=========================================== */
const Landing = () => {
  const { userLoggedIn } = useAuth();

  // auth modal
  const [authOpen, setAuthOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [err, setErr] = useState('');
// ADD — register modal state
const [registerOpen, setRegisterOpen] = useState(false);
const [rName, setRName] = useState('');
const [rEmail, setREmail] = useState('');
const [rPassword, setRPassword] = useState('');
const [rConfirm, setRConfirm] = useState('');
const [rRole, setRRole] = useState('Real Estate Agent');
const [rAccessCode, setRAccessCode] = useState('');
const [rIsRegistering, setRIsRegistering] = useState(false);
const [rError, setRError] = useState('');

// Computed from access code
const [accessToken, setAccessToken] = useState(0);
const [totalSearches, setTotalSearches] = useState(0);
const [marketResearchSearches, setMarketResearchSearches] = useState(0);
const [previousStudent, setPreviousStudent] = useState(false);
  // parallax hero + smooth nav scroll
  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      document.documentElement.style.setProperty('--parallax', `${Math.min(y * 0.12, 80)}px`);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });

    // Smooth-scroll for header links
    const nav = document.querySelector('header');
    const handler = (e) => {
      const a = e.target.closest('a[href^="#"]');
      if (!a) return;
      const id = a.getAttribute('href').slice(1);
      const el = document.getElementById(id);
      if (el) {
        e.preventDefault();
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    };
    nav?.addEventListener('click', handler);

    return () => {
      window.removeEventListener('scroll', onScroll);
      nav?.removeEventListener('click', handler);
    };
  }, []);

  // ADD — apply access-code logic for the register modal
useEffect(() => {
  const lower = rAccessCode.trim().toLowerCase();
  if (lower === 'cashflowing') {
    setAccessToken(4);
    setTotalSearches(20);
    setMarketResearchSearches(0);
    setPreviousStudent(false);
  } else if (lower === 'rei') {
    setAccessToken(5);
    setTotalSearches(100);
    setMarketResearchSearches(100);
    setPreviousStudent(false);
  } else if (lower === 'buildingwealth') {
    setAccessToken(5);
    setTotalSearches(100);
    setMarketResearchSearches(100);
    setPreviousStudent(true);
  } else {
    setAccessToken(0);
    setTotalSearches(0);
    setMarketResearchSearches(0);
    setPreviousStudent(false);
  }
}, [rAccessCode]);

  const progress = useTopProgress();
// ADD — register submit
const onRegister = async (e) => {
  e.preventDefault();
  if (rIsRegistering) return;
  if (rPassword !== rConfirm) {
    setRError('Passwords do not match');
    return;
  }
  if (accessToken === 0) {
    setRError('Please enter a valid access code');
    return;
  }
  try {
    setRIsRegistering(true);
    setRError('');
    await doCreateUserWithEmailAndPassword(
      rEmail,
      rPassword,
      rRole,
      rName,
      accessToken,
      totalSearches,
      marketResearchSearches,
      previousStudent
    );
  } catch (e2) {
    setRError(e2?.message || 'Registration failed.');
  } finally {
    setRIsRegistering(false);
  }
};
  const onSubmit = async (e) => {
    e.preventDefault();
    if (isSigningIn) return;
    try {
      setIsSigningIn(true);
      setErr('');
      await doSignInWithEmailAndPassword(email, password);
    } catch (e2) {
      setErr(e2?.message || 'Sign in failed.');
      setIsSigningIn(false);
    }
  };
  const onGoogle = async (e) => {
    e.preventDefault();
    if (isSigningIn) return;
    try {
      setIsSigningIn(true);
      await doSignInWithGoogle();
    } catch (e2) {
      setErr(e2?.message || 'Google sign-in failed.');
      setIsSigningIn(false);
    }
  };

  if (userLoggedIn) return <Navigate to="/dealexplorer" replace />;

  return (
    <div className="relative min-h-screen overflow-x-hidden bg-white text-gray-900">
      {/* Top scroll progress */}
      <div
        aria-hidden
        className="fixed top-0 left-0 right-0 z-[60] h-[3px]"
        style={{ transform: `scaleX(${progress})`, transformOrigin: '0 50%', backgroundColor: BRAND_GREEN }}
      />

      {/* Ambient background */}
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 -z-10"
        style={{
          background:
            `radial-gradient(1200px 600px at 70% -10%, ${BRAND_GREEN}40, transparent 60%), radial-gradient(900px 500px at -10% 30%, ${BRAND_GREEN}2E, transparent 60%)`,
        }}
      />

      {/* NAV */}
      <header className="sticky top-0 z-50 bg-white/70 backdrop-blur-md border-b border-black/5">
        <div className="mx-auto max-w-7xl px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src="https://i.ibb.co/F4Lz1QN/Image-1-10-25-at-5-32-PM.jpg"
              alt="Elevate REI"
              className="w-40 h-12 rounded"
            />
          </div>
          <nav className="hidden md:flex items-center gap-8 text-sm">
            <a href="#features" className="hover:text-black/70">Features</a>
            <a href="#stats" className="hover:text-black/70">Proof</a>
            <a href="#how" className="hover:text-black/70">How it works</a>
            <a href="#cta" className="hover:text-black/70">Get the app</a>
          </nav>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setAuthOpen(true)}
              className="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 transition"
            >
              Sign in
            </button>
           <Link
  to="/register"
  onClick={(e) => { e.preventDefault(); setRegisterOpen(true); }}
  className="px-4 py-2 rounded-lg text-white font-medium hover:bg-black transition transform-gpu hover:scale-[1.02]"
  style={{ backgroundColor: BRAND_GREEN }}
>
  Create account
</Link>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="relative py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-4 grid md:grid-cols-2 gap-10 items-start">
          {/* left: text */}
          <div className="relative z-10 self-center">
            <Fade>
              <h1 className="text-4xl md:text-4xl font-semibold tracking-tight leading-[1.05] text-black">
                Real Estate Investing. <br /> Made Simple.
              </h1>
            </Fade>
            <Fade delay={120}>
              <p className="mt-5 text-lg text-gray-600 max-w-md">
                Elevate REI is your all-in-one real estate investing tool.
                <br />
                Whether you're just getting started or scaling up, Elevate helps you find, analyze, and act on both on-market and
                off-market deals with ease.
              </p>
            </Fade>
            <Fade delay={220}>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link
                  to="https://apps.apple.com/us/app/elevate-rei/id6741596876"
                  className="px-6 py-3 rounded-2xl text-white font-medium hover:shadow-xl transition transform-gpu hover:scale-[1.02]"
                  style={{ backgroundColor: BRAND_GREEN }}
                >
                  Download & Start Free
                </Link>
                <button
                  onClick={() =>
                    document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })
                  }
                  className="px-6 py-3 rounded-2xl border border-gray-300 hover:bg-gray-50 transition"
                >
                  See features
                </button>
              </div>
            </Fade>
          </div>

          {/* right: cover image */}
          <div className="relative flex justify-center z-0 self-center md:self-start mt-8 md:mt-12 mr-6 md:mr-12">
            <img
              src={newCover}
              alt="Elevate app cover"
              className="
                h-auto object-contain select-none pointer-events-none
                w-full max-w-none
                md:w-[140%] lg:w-[140%]
              "
              style={{ transform: 'translateY(calc(var(--parallax) * 0.15))' }}
            />
          </div>
        </div>
      </section>

      {/* STATS */}
      <section id="stats" className="py-18 md:py-24">
        <div className="mx-auto max-w-7xl px-4">
          <div className="grid md:grid-cols-3 gap-6 text-center">
            <Fade>
              <div className="rounded-2xl border border-black/10 shadow-xl p-8">
                <div className="text-sm uppercase tracking-widest text-gray-500">Listings Analyzed</div>
                <CountUp to={200000} duration={1400} suffix="+" className="mt-2 text-4xl font-semibold" />
              </div>
            </Fade>
            <Fade delay={100}>
              <div className="rounded-2xl border border-black/10 shadow-xl p-8">
                <div className="text-sm uppercase tracking-widest text-gray-500">Investors</div>
                <CountUp to={2500} duration={1500} suffix="+" className="mt-2 text-4xl font-semibold" />
              </div>
            </Fade>
            <Fade delay={200}>
              <div className="rounded-2xl border border-black/10 shadow-xl p-8">
                <div className="text-sm uppercase tracking-widest text-gray-500">Offers Made</div>
                <CountUp to={30000000} duration={1600} prefix="$" suffix="+" className="mt-2 text-4xl font-semibold" />
              </div>
            </Fade>
          </div>
        </div>
      </section>

      {/* FEATURES */}
     {/* FEATURES */}
<section id="features" className="relative py-20 md:py-28 bg-gradient-to-b from-white to-gray-50">
  <div className="mx-auto max-w-7xl px-4">
    <Fade>
      <h2 className="text-3xl md:text-5xl font-semibold tracking-tight text-center text-black">
        Everything you need to invest—faster.
      </h2>
    </Fade>

    {(() => {
      // Map your named features to explicit icons
      // Reusing your existing inline FeatureIcons array indices:
      // 0: Magnifier, 1: Home, 2: Chart, 3: Dollar, 4: Calculator,
      // 5: Chat, 6: Users, 7: Banknotes
      const FEATURES = [
        { title: 'Property Search & Analysis', copy: 'View listings with estimated payments.', Icon: FeatureIcons[0] },
        { title: 'Off-Market Deals',           copy: 'Get access to wholesale listings in real time.', Icon: FeatureIcons[1] },
        { title: 'Section 8 Insights',         copy: 'See Fair Market Rents by zip code.', Icon: FeatureIcons[2] },
        { title: 'Rent Reveal',                copy: 'Know the max rent you can charge.', Icon: FeatureIcons[3] },
        { title: 'Deal Calculator',            copy: 'Analyze cash flow, ROI, and more.', Icon: FeatureIcons[4] },
        { title: 'AI Assistant',               copy: 'Ask real estate questions and get instant answers.', Icon: FeatureIcons[5] },
        { title: 'Skip Tracing',               copy: 'Contact owners quickly and easily.', Icon: FeatureIcons[6] },
        // Split the old combined tile into two separate ones:
        { title: 'Lending',                    copy: 'We pair you with vetted lenders—no guesswork.', Icon: FeatureIcons[7] },
        { title: 'Insurance',                  copy: 'Get matched with trusted insurance partners fast.', Icon: ShieldCheckIcon },
      ];

      return (
        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {FEATURES.map(({ title, copy, Icon }, i) => (
            <Fade key={title} delay={i * 60}>
              <div className="p-6 rounded-2xl border border-black/10 shadow-xl bg-white hover:shadow-2xl transition">
                <div className="flex items-center gap-3">
                  <Icon className="w-6 h-6" style={{ color: BRAND_GREEN }} />
                  <div className="font-semibold text-lg text-black">{title}</div>
                </div>
                <p className="mt-2 text-gray-600">{copy}</p>
              </div>
            </Fade>
          ))}
        </div>
      );
    })()}

    <Fade delay={120}>
      <p className="mt-10 max-w-3xl mx-auto text-center text-sm text-gray-500">
        Built for Investors: Save time, make smarter decisions, and access powerful tools—right from your phone.
        <br />
        <span className="block mt-2">
          <strong>Disclaimer:</strong> All data and calculations are estimates. Always consult a licensed professional
          before making investment decisions.
        </span>
      </p>
    </Fade>
  </div>
</section>

      {/* HOW IT WORKS — Sticky AI stage */}
{/* HOW IT WORKS — Three identical phones with captions */}
<section id="how" className="relative bg-gray-50 py-24">
  <Fade>
    <h2 className="text-3xl md:text-5xl font-semibold tracking-tight mb-16 text-center text-black">
      How Elevate AI works
    </h2>
  </Fade>

  {/* Shared size constants + Phone component (inline to this file) */}
  {(() => {
    const PHONE_W = "w-[300px]";
    const PHONE_H = "h-[680px]";

    const Phone = ({ src }) => (
      <div
        className={`relative ${PHONE_W} ${PHONE_H} rounded-[2rem] border border-black/10 shadow-2xl overflow-hidden bg-white`}
      >
        {/* Removed fake status bar and the dark gradient overlay */}
        <video
          src={src}
          autoPlay
          muted
          loop
          playsInline
          className="absolute inset-0 w-full h-full object-cover scale-[1]"
        />
      </div>
    );

    return (
      <div className="mx-auto max-w-7xl px-4">
        {/* One grid: phone + caption per column, all centered */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 place-items-center">
          {/* Phone + Caption 1 */}
          <div className="flex flex-col items-center text-center">
            <Phone src={rentreveal} />
            <div
              className={`mt-6 ${PHONE_W} p-4 rounded-2xl border shadow-xl bg-white flex flex-col items-center`}
            >
              <HomeIcon className="w-8 h-8 text-[#06b22b]" />
              <h3 className="mt-3 font-semibold text-lg text-black">Rent Reveal AI</h3>
              <p className="mt-2 text-gray-600">Instant rent + market insights in seconds.</p>
            </div>
          </div>

          {/* Phone + Caption 2 */}
          <div className="flex flex-col items-center text-center">
            <Phone src={dealexplorerai} />
            <div
              className={`mt-6 ${PHONE_W} p-4 rounded-2xl border shadow-xl bg-white flex flex-col items-center`}
            >
              <ChartBarIcon className="w-8 h-8 text-[#06b22b]" />
              <h3 className="mt-3 font-semibold text-lg text-black">Deal Explorer AI</h3>
              <p className="mt-2 text-gray-600">AI comps, cash flow & ROI — fast.</p>
            </div>
          </div>

          {/* Phone + Caption 3 */}
          <div className="flex flex-col items-center text-center">
            <Phone src={aiassistant} />
            <div
              className={`mt-6 ${PHONE_W} p-4 rounded-2xl border shadow-xl bg-white flex flex-col items-center`}
            >
              <MicrophoneIcon className="w-8 h-8 text-[#06b22b]" />
              <h3 className="mt-3 font-semibold text-lg text-black">Voice Assistant</h3>
              <p className="mt-2 text-gray-600">Hands-free property answers on the go.</p>
            </div>
          </div>
        </div>
      </div>
    );
  })()}
</section>

      {/* CTA */}
      <section id="cta" className="py-24">
        <div className="mx-auto max-w-5xl px-4 text-center">
          <Fade>
            <h3 className="text-3xl md:text-5xl font-semibold tracking-tight text-black">
              Download now and start investing smarter with Elevate REI.
            </h3>
          </Fade>
          <Fade delay={120}>
            <div className="mt-8 flex items-center justify-center gap-3">
              <Link
  to="/register"
  onClick={(e) => { e.preventDefault(); setRegisterOpen(true); }}
  className="px-6 py-3 rounded-xl text-white font-medium hover:bg-black transition transform-gpu hover:scale-[1.02]"
  style={{ backgroundColor: BRAND_GREEN }}
>
  Get started free
</Link>
              <button
                onClick={() => setAuthOpen(true)}
                className="px-6 py-3 rounded-xl border border-gray-300 hover:bg-gray-50 transition"
              >
                I already have an account
              </button>
            </div>
          </Fade>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-black/5 py-10">
        <div className="mx-auto max-w-7xl px-4 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <img
              src="https://i.ibb.co/F4Lz1QN/Image-1-10-25-at-5-32-PM.jpg"
              alt="Elevate"
              className="w-8 h-8 rounded"
            />
            <span className="text-sm text-gray-600">© {new Date().getFullYear()} Elevate REI</span>
          </div>
          <div className="flex gap-6 text-sm text-gray-600">
            <a href="#features" className="hover:text-black/70">Features</a>
            <a href="#stats" className="hover:text-black/70">Proof</a>
            <a href="#how" className="hover:text-black/70">How it works</a>
            <a href="#cta" className="hover:text-black/70">Get the app</a>
          </div>
        </div>
      </footer>

      {/* AUTH MODAL */}
{authOpen && (
  <div
    className="fixed inset-0 z-[70] grid place-items-center bg-black/40 p-4"
    role="dialog"
    aria-modal="true"
  >
    <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl border border-black/10 relative">
      
      {/* Close button (X) */}
      <button
        onClick={() => setAuthOpen(false)}
        className="absolute top-4 right-4 p-2 rounded-lg hover:bg-gray-100"
        aria-label="Close"
      >
        ✕
      </button>

      {/* Logo up top */}
      <div className="flex justify-center mb-6">
        <img
          src="https://i.ibb.co/F4Lz1QN/Image-1-10-25-at-5-32-PM.jpg"
          alt="Elevate REI Logo"
          className="w-60 h-20 object-contain"
        />
      </div>

      <form onSubmit={onSubmit} className="mt-4 space-y-4">
        <div>
          <label className="text-sm font-medium text-gray-700">Email</label>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mt-1 w-full rounded-lg border px-3 py-2 outline-none focus:ring-2"
            style={{ borderColor: '#e5e7eb', boxShadow: `0 0 0 0 rgba(0,0,0,0)` }}
            onFocus={(e) =>
              (e.target.style.boxShadow = `0 0 0 2px ${BRAND_GREEN}40`)
            }
            onBlur={(e) => (e.target.style.boxShadow = 'none')}
          />
        </div>
        <div>
          <label className="text-sm font-medium text-gray-700">Password</label>
          <input
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="mt-1 w-full rounded-lg border px-3 py-2 outline-none focus:ring-2"
            style={{ borderColor: '#e5e7eb', boxShadow: `0 0 0 0 rgba(0,0,0,0)` }}
            onFocus={(e) =>
              (e.target.style.boxShadow = `0 0 0 2px ${BRAND_GREEN}40`)
            }
            onBlur={(e) => (e.target.style.boxShadow = 'none')}
          />
        </div>
        {err && <div className="text-sm text-red-600 font-medium">{err}</div>}

        <button
          type="submit"
          disabled={isSigningIn}
          className="w-full px-4 py-2 rounded-lg text-white font-medium transition"
          style={{
            backgroundColor: isSigningIn ? '#d1d5db' : BRAND_GREEN,
            cursor: isSigningIn ? 'not-allowed' : 'pointer',
          }}
        >
          {isSigningIn ? 'Signing In…' : 'Sign In'}
        </button>

        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t" />
          </div>
          <div className="relative flex justify-center">
            <span className="bg-white px-2 text-xs text-gray-500">OR</span>
          </div>
        </div>

        <button
          onClick={onGoogle}
          disabled={isSigningIn}
          className={`w-full flex items-center justify-center gap-3 border rounded-lg py-2.5 ${
            isSigningIn ? 'cursor-not-allowed' : 'hover:bg-gray-50'
          }`}
        >
          {/* Google icon */}
          <svg
            className="w-5 h-5"
            viewBox="0 0 48 48"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <g clipPath="url(#g)">
              <path
                d="M47.5 24.55c0-1.63-.13-3.27-.41-4.87H24.48v9.24h12.96c-.54 2.98-2.27 5.62-4.81 7.3v5.99h7.74C44.92 38.03 47.5 31.85 47.5 24.55Z"
                fill="#4285F4"
              />
              <path
                d="M24.48 48c6.47 0 11.93-2.12 15.91-5.79l-7.74-6c-2.15 1.46-4.93 2.29-8.17 2.29-6.26 0-11.56-4.22-13.47-9.9H3.03v6.18C7.11 42.89 15.4 48 24.48 48Z"
                fill="#34A853"
              />
              <path
                d="M11 28.6a14.48 14.48 0 0 1 0-9.19V13.23H3.03a24 24 0 0 0 0 21.56l7.97-6.18Z"
                fill="#FBBC04"
              />
              <path
                d="M24.48 9.5c3.42-.05 6.73 1.23 9.21 3.6l6.85-6.85C36.2 2.17 30.44-0.07 24.48 0 15.41 0 7.11 5.12 3.03 13.23l7.97 6.18C12.9 13.72 18.22 9.5 24.48 9.5Z"
                fill="#EA4335"
              />
            </g>
            <defs>
              <clipPath id="g">
                <rect width="48" height="48" fill="white" />
              </clipPath>
            </defs>
          </svg>
          Continue with Google
        </button>

        <p className="text-center text-sm">
          New here?{' '}
          <Link
            to="/register"
            className="font-semibold hover:underline"
            onClick={() => setAuthOpen(false)}
          >
            Create an account
          </Link>
        </p>
      </form>
    </div>
  </div>
)}

{/* REGISTER MODAL */}
{registerOpen && (
  <div
    className="fixed inset-0 z-[70] grid place-items-center bg-black/40 p-4"
    role="dialog"
    aria-modal="true"
  >
    <div
      className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl border border-black/10 relative max-h-[90vh] overflow-y-auto overscroll-contain"
    >
      {/* Close button (X) */}
      <button
        onClick={() => setRegisterOpen(false)}
        className="absolute top-4 right-4 p-2 rounded-lg hover:bg-gray-100"
        aria-label="Close"
      >
        ✕
      </button>

      {/* Logo up top */}
      <div className="flex justify-center mb-6">
        <img
          src="https://i.ibb.co/F4Lz1QN/Image-1-10-25-at-5-32-PM.jpg"
          alt="Elevate REI Logo"
          className="w-60 h-20 object-contain"
        />
      </div>

      <form onSubmit={onRegister} className="mt-2 space-y-4">
        {/* Name */}
        <div>
          <label className="text-sm font-medium text-gray-700">Name</label>
          <input
            type="text"
            required
            value={rName}
            onChange={(e) => setRName(e.target.value)}
            className="mt-1 w-full rounded-lg border px-3 py-2 outline-none"
            style={{ borderColor: '#e5e7eb' }}
          />
        </div>

        {/* Email */}
        <div>
          <label className="text-sm font-medium text-gray-700">Email</label>
          <input
            type="email"
            required
            value={rEmail}
            onChange={(e) => setREmail(e.target.value)}
            className="mt-1 w-full rounded-lg border px-3 py-2 outline-none"
            style={{ borderColor: '#e5e7eb' }}
          />
        </div>

        {/* Password */}
        <div>
          <label className="text-sm font-medium text-gray-700">Password</label>
          <input
            type="password"
            required
            value={rPassword}
            onChange={(e) => setRPassword(e.target.value)}
            className="mt-1 w-full rounded-lg border px-3 py-2 outline-none"
            style={{ borderColor: '#e5e7eb' }}
          />
        </div>

        {/* Confirm Password */}
        <div>
          <label className="text-sm font-medium text-gray-700">Confirm Password</label>
          <input
            type="password"
            required
            value={rConfirm}
            onChange={(e) => setRConfirm(e.target.value)}
            className="mt-1 w-full rounded-lg border px-3 py-2 outline-none"
            style={{ borderColor: '#e5e7eb' }}
          />
        </div>

        {/* Role */}
        <div>
          <label className="text-sm font-medium text-gray-700">I am a…</label>
          <select
            value={rRole}
            onChange={(e) => setRRole(e.target.value)}
            className="mt-1 w-full rounded-lg border px-3 py-2 outline-none bg-white"
            style={{ borderColor: '#e5e7eb' }}
          >
            <option>Real Estate Agent</option>
            <option>Aspiring Investor</option>
            <option>Student</option>
          </select>
        </div>

        {/* Access Code */}
        <div>
          <label className="text-sm font-medium text-gray-700">Access Code</label>
          <input
            type="text"
            required
            value={rAccessCode}
            onChange={(e) => setRAccessCode(e.target.value)}
            className="mt-1 w-full rounded-lg border px-3 py-2 outline-none"
            style={{ borderColor: '#e5e7eb' }}
          />
        </div>

        {rError && <div className="text-sm text-red-600 font-medium">{rError}</div>}

        <button
          type="submit"
          disabled={rIsRegistering || accessToken === 0}
          className="w-full px-4 py-2 rounded-lg text-white font-medium transition"
          style={{
            backgroundColor: (rIsRegistering || accessToken === 0) ? '#9ca3af' : BRAND_GREEN,
            cursor: (rIsRegistering || accessToken === 0) ? 'not-allowed' : 'pointer',
          }}
        >
          {rIsRegistering ? 'Signing Up…' : 'Sign Up'}
        </button>

     

       

        <p className="text-center text-sm">
          Already have an account?{' '}
          <button
            type="button"
            className="font-semibold hover:underline"
            onClick={() => { setRegisterOpen(false); setAuthOpen(true); }}
          >
            Sign in
          </button>
        </p>
      </form>
    </div>
  </div>
)}
    </div>
  );
};

export default Landing;